import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { routes } from '@platform/core/lib/routes';
import { tagColor } from '@platform/core/lib/statusColors';
import { useCreateTagMutation, useTagsQuery } from '@platform/state/api';
import { TagInput } from '@platform/ui';
import type { IUnderTitleTagsProps } from './IUnderTitleTagsProps';

/** Відтінок нового тега (прототип createTag): (кількість тегів × 47 + 200) mod 360. */
const nextHue = (count: number): number => (count * 47 + 200) % 360;

/**
 * Теги під назвою (UIUX forms/form-structure, controls/tag-input; прототип v4): поле-рамка від 40px (рамка 1.5px, радіус 10) з чипами
 * й введенням; невідоме ім'я створює тег у словнику робочого простору. У режимі редагування зміни йдуть у чернетку; у режимі перегляду
 * поле сіре, але × і введення лишаються, а зміна зберігається одразу (прототип setVal поза editRec) — показується до відповіді сервера.
 * Запис, який сервер не дозволяє змінювати, — лише читання: чипи без × і «Без тегів».
 */
export function UnderTitleTags({ ws, record, editor, readOnly, label }: IUnderTitleTagsProps) {
  const { t } = useTranslation();
  const { data: all = [] } = useTagsQuery(ws);
  const [createTag] = useCreateTagMutation();
  const [pending, setPending] = useState<{ tags: string[]; version: number } | null>(null);
  const locked = record.permissions?.edit === false;
  const live = readOnly && !editor.editing && !locked;
  const names = live && pending?.version === record.rowVersion ? pending.tags : editor.tags;
  const byName = useMemo(() => new Map(all.map((x) => [x.name.toLowerCase(), x])), [all]);
  const tags = useMemo(() => all.map((x) => ({ key: x.name, label: x.name, color: tagColor(x.hue) })), [all]);
  const chips = names.map((n) => ({ key: n, label: n, color: byName.has(n.toLowerCase()) ? tagColor(byName.get(n.toLowerCase())!.hue) : undefined }));
  const apply = (next: string[]) => {
    if (!live) { editor.setTags(next); return; }
    setPending({ tags: next, version: record.rowVersion });
    void editor.patchNow({}, next).then((ok) => { if (!ok) setPending(null); });
  };
  const add = (name: string) => {
    if (!name || names.some((n) => n.toLowerCase() === name.toLowerCase())) return;
    const known = byName.get(name.toLowerCase());
    apply([...names, known?.name ?? name]);
    if (!known) void createTag({ ws, body: { name, hue: nextHue(all.length) } });
  };
  return (
    <div className={cx('rec-ut', live && 'rec-ut--live')}>
      <label className="field__label fc-label" htmlFor="rec-tags">{label}</label>
      <TagInput id="rec-tags" variant="box" label={t('recordPage.general.tagLabel')} chips={chips} tags={tags} onAdd={add} readOnly={readOnly && !live}
        onRemove={(k) => apply(names.filter((n) => n !== k))} placeholder={t('recordPage.general.tagPlaceholder')} showEmpty={live}
        placeholderMore={t('recordPage.general.tagPlaceholderMore')} emptyText={t('recordPage.general.noTags')}
        manage={<Link className="pk-list__link" to={routes.module('tag')} onMouseDown={(e) => e.preventDefault()}>{t('recordPage.general.manageTags')}</Link>} />
    </div>
  );
}
